import React, { useState, useRef, useEffect } from 'react';
import faqData from '../data/faqs.json';

function FAQSection() {
    const [openIndex, setOpenIndex] = useState(null);
    const [closingIndex, setClosingIndex] = useState(null);
    const contentRefs = useRef([]);

    useEffect(() => {
        contentRefs.current = contentRefs.current.slice(0, faqData.faqs.length);
    }, []);

    const toggleFAQ = (index) => {
        if (openIndex === index) {
            setClosingIndex(index);
            setOpenIndex(null);
            // Remove closing state after animation completes
            setTimeout(() => setClosingIndex(null), 300);
        } else {
            setOpenIndex(index);
            setClosingIndex(null);
        }
    };

    // Split FAQs into two arrays for two columns
    const midPoint = Math.ceil(faqData.faqs.length / 2);
    const leftColumnFaqs = faqData.faqs.slice(0, midPoint);
    const rightColumnFaqs = faqData.faqs.slice(midPoint);

    const FaqColumn = ({ faqs }) => (
        <div className="space-y-4">
            {faqs.map((faq, columnIndex) => {
                const globalIndex = faqs === leftColumnFaqs ? columnIndex : columnIndex + midPoint;
                const isOpen = openIndex === globalIndex;
                const isClosing = closingIndex === globalIndex;

                return (
                    <div 
                        key={globalIndex} 
                        className="bg-white rounded-xl shadow-md overflow-hidden transition-all duration-300 hover:shadow-lg"
                    >
                        <button
                            className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors group"
                            onClick={() => toggleFAQ(globalIndex)}
                        >
                            <span className="font-semibold text-lg text-primary pr-4 group-hover:text-primary/80">
                                {faq.question}
                            </span>
                            <span className={`flex-shrink-0 h-6 w-6 rounded-full border-2 border-primary flex items-center justify-center transition-all duration-300 ${
                                isOpen ? 'bg-primary transform rotate-180' : 'group-hover:bg-primary/10'
                            }`}>
                                <svg
                                    className={`w-4 h-4 transition-all duration-300 ${
                                        isOpen ? 'text-white' : 'text-primary'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </span>
                        </button>
                        <div
                            ref={el => contentRefs.current[globalIndex] = el}
                            style={{
                                '--content-height': `${contentRefs.current[globalIndex]?.scrollHeight}px`
                            }}
                            className={`transition-all duration-300 ease-out ${
                                isOpen ? 'animate-faq-open' :
                                isClosing ? 'animate-faq-close' : 'h-0 opacity-0'
                            }`}
                        >
                            <p className="px-6 pb-4 text-gray-600 leading-relaxed">
                                {faq.answer}
                            </p>
                        </div>
                    </div>
                );
            })}
        </div>
    );

    return (
        <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-16 animate-slideFromLeft">
                    <h2 className="text-4xl font-arial-rounded text-primary mb-4 relative inline-block">
                        Frequently Asked Questions
                        <div className="absolute -bottom-5 left-1/2 w-40 h-1 bg-primary-yellow transform -translate-x-1/2"></div>
                    </h2>
                    <p className="text-gray-600 mt-4 text-lg font-avenir">
                        Find answers to common questions about our services
                    </p>
                </div>
                <div className="grid md:grid-cols-2 gap-8">
                    <FaqColumn faqs={leftColumnFaqs} />
                    <FaqColumn faqs={rightColumnFaqs} />
                </div>
            </div>
        </section>
    );
}

export default FAQSection;
